// Shared adapter between the two guide content sources so the Guides hub can
// present them as one list.
//
// The two sources are NOT interchangeable, which is why this module exists:
//
//   API guides (`/guides/by-room`, `/guides/by-catalog`)
//     `description` is plain text. GuideDrawer runs a URL-autolinking regex
//     over it and renders the result.
//
//   Property sections (`room.propertyGuide`, authored in the dashboard's
//   Quill editor)
//     `description` is the section *title* and `text` is rich HTML. Running
//     the autolinker over that HTML would corrupt `href` attributes, and it
//     needs the `.ql-editor` styles to render correctly.
//
// So property sections keep their own viewer (`PropertySectionDrawer`) and
// carry a plain-text `excerpt` for card previews, while exposing the same
// `title` / `images` / `catalog` fields the shared card UI reads.

export const PROPERTY_CATALOG_ID = "property";
export const PROPERTY_CATALOG_NAME = "The Property";
export const ALL_CATALOG_ID = "all";
const PROPERTY_CATALOG = {
  id: PROPERTY_CATALOG_ID,
  name: PROPERTY_CATALOG_NAME,
};

// Quill output is trusted host-authored HTML, but card previews need flat
// text. Decoding happens through the DOM so entities (`&amp;`, `&nbsp;`)
// resolve the same way the browser would render them. Block-level tags
// become spaces first, otherwise "<p>a</p><p>b</p>" collapses to "ab".
export const htmlToExcerpt = (html) => {
  if (typeof html !== "string" || !html) return "";

  const spaced = html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/(p|div|li|h[1-6]|tr|blockquote)>/gi, " ");

  if (typeof document === "undefined") {
    return spaced.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }

  const el = document.createElement("div");
  el.innerHTML = spaced;
  return (el.textContent || "").replace(/\s+/g, " ").trim();
};

export const DIRECTIONS_ITEM_ID = "property-directions";

/**
 * Full street address for display. `room.address` is a JSON blob from the
 * Places autocomplete on most rooms but a bare string on older ones, so both
 * shapes are handled. Unlike MainV2's hero text this keeps the city and
 * country — the hub is where a guest double-checks where they're going.
 */
export const getFormattedAddress = (room) => {
  if (!room?.address || typeof room.address !== "string") return "";

  try {
    const parsed = JSON.parse(room.address);
    return parsed?.formatted_address || "";
  } catch (error) {
    // Not JSON — older rooms store the address as plain text.
    return room.address;
  }
};

/**
 * Turns a room's property guide into card-ready items for the hub.
 *
 * `imageUrl` is the already-resolved hero image MainV2 holds, used only for
 * the overview card so it isn't the one imageless card in a grid of photos.
 *
 * Directions lead the list: "how do I get there" is the first thing a guest
 * wants, and it keeps the map in the same hub as everything else rather than
 * in its own separate surface. It opens `DirectionsDrawer`, which needs the
 * room rather than any text of its own, so this item is a pure signpost.
 */
export const buildPropertyGuideItems = (room, imageUrl) => {
  const items = [];

  if (room?.address) {
    // Showing the address itself rather than generic copy — the whole point
    // of leading with this card is reminding a guest where they're headed.
    const address = getFormattedAddress(room);
    items.push({
      id: DIRECTIONS_ITEM_ID,
      kind: "directions",
      title: "Getting here",
      html: "",
      excerpt: address || "Map and directions to the property",
      images: [],
      catalog: PROPERTY_CATALOG,
    });
  }

  // The overview is synthesized from the room record rather than authored as
  // a section, matching what the old Explore sheet showed at the top.
  if (room?.description) {
    const overviewImage = imageUrl || room?.images?.[0];
    items.push({
      id: "property-overview",
      kind: "property",
      title: "Property Overview",
      html: room.description,
      excerpt: htmlToExcerpt(room.description),
      images: overviewImage ? [overviewImage] : [],
      catalog: PROPERTY_CATALOG,
    });
  }

  const sections = Array.isArray(room?.propertyGuide) ? room.propertyGuide : [];
  sections.forEach((section, index) => {
    if (!section) return;

    // A section with a title but no body is still worth listing — the title
    // alone ("Parking", "Quiet hours") carries information.
    const body = section.text || "";
    const title = section.description || `Section ${index + 1}`;

    items.push({
      id: `property-section-${index}`,
      kind: "property",
      title,
      html: body,
      excerpt: htmlToExcerpt(body),
      images: [],
      catalog: PROPERTY_CATALOG,
    });
  });

  return items;
};
